"use client";
import { useState, useEffect } from "react";
import { Clock, User, Calendar, MapPin } from "lucide-react";
import { fetchEvento } from "@/lib/api/eventos/fetchEvento";
import { Evento } from "@/lib/api/eventos/types";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import parse from "html-react-parser";
import { type Locale, intlTag } from "@/i18n/config";

function getMonthKey(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

function parseEventoDate(fecha: string) {
  const [year, month, day] = fecha.split("-").map(Number);

  if (!year || !month || !day) {
    return null;
  }

  const date = new Date(year, month - 1, day);

  if (
    Number.isNaN(date.getTime()) ||
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
}

export default function CalendarioEventosPage() {
  const t = useTranslations("eventosPage");
  const locale = useLocale();
  const localeCode = intlTag(locale);
  const hoy = new Date();
  const fechaMesActual = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
  const fechaMesSiguiente = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 1);
  const mesesDisponibles = [fechaMesActual, fechaMesSiguiente].map((date) => ({
    key: getMonthKey(date),
    label: date.toLocaleString(localeCode, {
      month: "long",
      year: "numeric",
    }),
  }));
  const [mesActivo, setMesActivo] = useState(() => mesesDisponibles[0].key);
  const [eventos, setEventos] = useState<Evento[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadEventos() {
      setLoading(true);
      try {
        const data = await fetchEvento(locale as Locale);
        setEventos(data);
      } catch (error) {
        console.error("Error fetching eventos:", error);
      } finally {
        setLoading(false);
      }
    }

    loadEventos();
  }, [locale]);

  // Van indexados por Date.getDay(), así que el orden arranca en domingo
  const diasSemana = t.raw("diasSemana") as string[];

  const fechaADiaSemana = (fecha: string) => {
    // parseEventoDate arma la fecha en hora local. new Date("2026-09-05")
    // la interpretaría como medianoche UTC y getDay() devolvería el día
    // anterior en Panamá (UTC-5).
    const date = parseEventoDate(fecha);
    return date ? diasSemana[date.getDay()] : "";
  };
  const horaAFormato12H = (hora: string) => {
    const [horas, minutos] = hora.split(":").map(Number);
    const periodo = horas >= 12 ? "PM" : "AM";
    const horas12 = horas % 12 || 12; // Convertir a formato 12H
    return `${horas12}:${minutos.toString().padStart(2, "0")} ${periodo}`;
  };

  const mesesPermitidos = new Set(mesesDisponibles.map((mes) => mes.key));
  const eventosFiltrados = eventos.filter((evento) => {
    const fechaEvento = parseEventoDate(evento.fecha);

    if (!fechaEvento) {
      return false;
    }

    const mesEvento = getMonthKey(fechaEvento);
    return mesesPermitidos.has(mesEvento) && mesEvento === mesActivo;
  });

  const mesActivoLabel =
    mesesDisponibles.find((mes) => mes.key === mesActivo)?.label ??
    mesesDisponibles[0].label;

  const getTerminalColor = (terminal: number) =>
    terminal == 1
      ? "bg-blue-500"
      : terminal === 2
        ? "bg-blue-600"
        : "bg-gray-600";

  const getTipoColor = (tipo: number) => {
    switch (tipo) {
      case 1:
        return "bg-blue-50 text-blue-700 border border-blue-200";
      case 2:
        return "bg-purple-50 text-purple-700 border border-purple-200";
      case 3:
        return "bg-green-50 text-green-700 border border-green-200";
      case 4:
        return "bg-orange-50 text-orange-700 border border-orange-200";
      default:
        return "bg-gray-50 text-gray-700 border border-gray-200";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-gray-100">
      <div className="relative overflow-hidden ">
        {/* Imagen de fondo */}
        <Image
          src="https://galeria.tocumenaero.com/gallery/Eventos/web-02.jpg"
          alt="Eventos Header"
          fill
          className="object-cover"
          priority
        />

        {/* Overlay oscuro para legibilidad del texto */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/20"></div>

        {/* Contenido */}
        <div className="relative z-10 container mx-auto px-4 py-16 md:py-24 max-w-[80%] h-full flex flex-col justify-center">
          <h1 className="text-5xl md:text-7xl font-bold text-white mt-16 mb-6 leading-tight">
            {t("title1")}
            <br />
            <span className="bg-gradient-to-r from-blue-200 to-white bg-clip-text text-transparent">
              {t("title2")}
            </span>
          </h1>

          {/* Selector de mes mejorado */}
          <div className="flex gap-3 flex-wrap">
            {mesesDisponibles.map((mes) => (
              <button
                key={mes.key}
                onClick={() => setMesActivo(mes.key)}
                className={`px-8 py-3 rounded-xl font-semibold text-base transition-all duration-300 ${
                  mesActivo === mes.key
                    ? "bg-white text-blue-700 shadow-xl scale-105"
                    : "bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 border border-white/20"
                }`}
              >
                {mes.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Grid de eventos */}
      <div className="container mx-auto px-4 py-16 max-w-[80%]">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading
            ? Array.from({ length: 6 }).map((_, index) => (
                <div
                  key={`skeleton-${index}`}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col min-h-105 border border-gray-100 animate-pulse"
                >
                  <div className="p-6 bg-gray-900/95 space-y-4 shrink-0">
                    <div className="flex items-start justify-between">
                      <div className="space-y-2">
                        <div className="h-12 w-16 rounded bg-white/15" />
                        <div className="h-3 w-12 rounded bg-white/10" />
                      </div>
                      <div className="h-9 w-24 rounded-lg bg-white/10" />
                    </div>
                    <div className="h-7 w-28 rounded-lg bg-blue-400/30" />
                  </div>

                  <div className="p-6 flex-1 flex flex-col bg-gradient-to-b from-white to-gray-50 space-y-4">
                    <div className="h-7 w-24 rounded-lg bg-gray-200" />
                    <div className="h-6 w-11/12 rounded bg-gray-300" />
                    <div className="space-y-2">
                      <div className="h-4 w-full rounded bg-gray-200" />
                      <div className="h-4 w-10/12 rounded bg-gray-200" />
                      <div className="h-4 w-8/12 rounded bg-gray-200" />
                    </div>
                    <div className="mt-auto pt-4 border-t border-gray-100 flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-gray-200" />
                      <div className="h-4 w-1/2 rounded bg-gray-200" />
                    </div>
                  </div>

                  <div className="h-1 bg-gray-200" />
                </div>
              ))
            : eventosFiltrados.map((evento) => (
                <div
                  key={evento.id}
                  className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 flex flex-col min-h-105 border border-gray-100"
                >
                  {/* Header del evento con fecha - diseño moderno */}
                  <div className="relative bg-gradient-to-br from-gray-800 via-gray-900 to-black text-white p-6 overflow-hidden shrink-0">
                    {/* Efecto decorativo */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-full blur-2xl group-hover:bg-blue-500/20 transition-all duration-500"></div>

                    <div className="relative z-10 flex items-start justify-between mb-4">
                      <div className="flex flex-col">
                        <div className="text-5xl font-bold leading-none mb-1 bg-gradient-to-br from-white to-blue-200 bg-clip-text text-transparent">
                          {evento.fecha.split("-")[2]}
                        </div>
                        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                          {fechaADiaSemana(evento.fecha)}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-2 text-sm bg-white/10 backdrop-blur-sm px-3 py-2 rounded-lg">
                          <Clock className="w-4 h-4 text-blue-300" />
                          <span className="font-semibold">
                            {horaAFormato12H(evento.hora)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Terminal badge mejorado */}
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-blue-300" />
                      <div
                        className={`inline-block px-4 py-1.5 rounded-lg text-xs font-semibold text-white ${getTerminalColor(
                          evento.terminal_id,
                        )} shadow-lg`}
                      >
                        Terminal {evento.terminal_id}
                      </div>
                    </div>
                  </div>

                  {/* Contenido del evento */}
                  <div className="p-6 flex-1 flex flex-col bg-gradient-to-b from-white to-gray-50">
                    <div
                      className={`inline-block px-3 py-1.5 rounded-lg text-xs font-semibold mb-4 w-fit ${getTipoColor(
                        evento.tag.id,
                      )}`}
                    >
                      {evento.tag.nombre}
                    </div>

                    <h3 className="text-lg font-bold text-gray-900 mb-3 leading-tight group-hover:text-blue-700 transition-colors duration-300">
                      {evento.titulo}
                    </h3>

                    <div className="text-gray-600 text-sm leading-relaxed flex-1 overflow-hidden mb-4">
                      {parse(evento.descripcion)}
                    </div>

                    <div className="flex items-center gap-2 text-sm text-gray-500 mt-auto pt-4 border-t border-gray-100">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                        <User className="w-4 h-4 text-blue-600" />
                      </div>
                      <span className="font-medium text-gray-700 line-clamp-1">
                        {evento.artista}
                      </span>
                    </div>
                  </div>

                  {/* Footer decorativo sutil */}
                  <div className="h-1 bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 shrink-0"></div>
                </div>
              ))}
        </div>

        {/* Mensaje si no hay eventos */}
        {!loading && eventosFiltrados.length === 0 && (
          <div className="text-center py-20 bg-white rounded-2xl shadow-lg border border-gray-100">
            <div className="w-20 h-20 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-6">
              <Calendar className="w-10 h-10 text-blue-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              {t("noEvents")}
            </h3>
            <p className="text-gray-500">
              {t("noEventsParagraph")} {mesActivoLabel}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
